import actionTypes from '../actions/types';
import { handleUpdateAccoutInfo, handleUpdateExecute } from '../store/handles';
import { handleMarginUpdateExecute, handleUpdateMarginAccoutInfo } from '../store/handlesMargin';

import * as Depth from './depth';
import * as Tickers from './tickers';
import * as Trades from './trades';

export const INITIAL_STATE = {
  klineUpdates: {},
};

const handleUpdateKlineData = (state, symbol, data) => ({
  ...state,
  klineUpdates: {
    ...state.klineUpdates,
    [symbol]: {
      [data.k.i]: data,
    },
  },
});

const handleSocketReceive = (state, { payload: { data } }) => {
  // tickers data is an array, others (trades, depth, kline) are not
  const ev = Array.isArray(data) ? data[0].e : data.e;
  switch (ev) {
    case '24hrMiniTicker':
      return Tickers.handleUpdateTickers(state, data);
    case 'aggTrade':
      return Trades.handleUpdateTradesData(state, { symbol: data.s, data: [data] });
    case 'depthUpdate':
      return Depth.handleUpdateDepthData(state, {
        symbol: data.s,
        data: {
          asks: data.a,
          bids: data.b,
          lastUpdateId: data.u,
          firstUpdateId: data.U,
        },
      }, true);
    case 'kline':
      return handleUpdateKlineData(state, data.s, data);
    default:
      if (__DEV__) console.warn('unknown update received:', ev);
      return state;
  }
};

const handleSocketReceiveMarginUpdate = (state, { payload }) => {
  let hasExe = false;
  let hasAcc = false;
  const result = {};
  const accoutInfo = [];
  const item = payload;
  if (item.e === 'executionReport') {
    const dataTmp = item;
    result.event = dataTmp.e;
    result.eventTime = dataTmp.E;
    result.symbol = dataTmp.s;
    result.clOrdId = dataTmp.c;
    result.side = dataTmp.S;
    result.orderType = dataTmp.o;
    result.timeInForce = dataTmp.f;
    result.qty = dataTmp.q;
    result.price = dataTmp.p;
    result.stopPrice = dataTmp.P;
    result.maxFloor = dataTmp.F;
    result.originalClOrdId = dataTmp.C;
    result.executionType = dataTmp.x;
    result.orderStatus = dataTmp.X;
    result.orderRejectReason = dataTmp.r;
    result.orderId = dataTmp.i;
    result.lastQty = dataTmp.l;
    result.cummulativeQty = dataTmp.z;
    result.lastPrice = dataTmp.L;
    result.commission = dataTmp.n;
    result.commissionAsset = dataTmp.N;
    result.time = dataTmp.T;
    result.executionId = dataTmp.I;
    result.tradeId = dataTmp.t;
    result.workingIndicator = dataTmp.w;
    result.isMaker = dataTmp.m;
    result.commissionAsset = dataTmp.N;

    hasExe = true;
  } else if (item.e === 'outboundAccountInfo') {
    accoutInfo.push(item);
    hasAcc = true;
  }
  if (hasExe) return handleMarginUpdateExecute(state, result);
  if (hasAcc) return handleUpdateMarginAccoutInfo(state, accoutInfo);

  return state;
};

// applies a batch of updates
const handleSocketReceiveBatch = (state, { payload }) => {
  const tickers = [];
  const trades = { symbol: null, data: [] };
  const depths = {
    symbol: null,
    data: {
      asks: [], bids: [], lastUpdateId: null, firstUpdateId: 0,
    },
  };
  const accoutInfo = [];
  const execuInfo = [];
  let hasTickers = false;
  let hasTrades = false;
  let hasDepths = false;
  let hasExe = false;
  let hasAcc = false;
  // todo batch array? split kline
  payload.forEach(item => {
    const { data } = (item.data ? item : { data: {} });
    if (Array.isArray(data)) {
      tickers.push(...data);
      hasTickers = true;
    } else if (data.e === 'kline') {
      state = handleUpdateKlineData(state, data.s, data);
    } else if (data.e === 'aggTrade') {
      trades.symbol = data.s;
      trades.data.push(data);
      hasTrades = true;
    } else if (data.e === 'depthUpdate') {
      depths.symbol = data.s;
      depths.data.asks.push(...data.a);
      depths.data.bids.push(...data.b);
      depths.data.lastUpdateId = data.u;
      depths.data.firstUpdateId = data.U;
      hasDepths = true;
    } else if (item.e === 'executionReport') {
      const dataTmp = item;
      const result = {};
      result.event = dataTmp.e;
      result.eventTime = dataTmp.E;
      result.symbol = dataTmp.s;
      result.clOrdId = dataTmp.c;
      result.side = dataTmp.S;
      result.orderType = dataTmp.o;
      result.timeInForce = dataTmp.f;
      result.qty = dataTmp.q;
      result.price = dataTmp.p;
      result.stopPrice = dataTmp.P;
      result.maxFloor = dataTmp.F;
      result.originalClOrdId = dataTmp.C;
      result.executionType = dataTmp.x;
      result.orderStatus = dataTmp.X;
      result.orderRejectReason = dataTmp.r;
      result.orderId = dataTmp.i;
      result.lastQty = dataTmp.l;
      result.cummulativeQty = dataTmp.z;
      result.lastPrice = dataTmp.L;
      result.commission = dataTmp.n;
      result.commissionAsset = dataTmp.N;
      result.time = dataTmp.T;
      result.executionId = dataTmp.I;
      result.tradeId = dataTmp.t;
      result.workingIndicator = dataTmp.w;
      result.isMaker = dataTmp.m;
      result.commissionAsset = dataTmp.N;

      execuInfo.push(result);
      hasExe = true;
    } else if (item.e === 'outboundAccountInfo') {
      accoutInfo.push(item);
      hasAcc = true;
    }
  });
  if (hasTickers) state = Tickers.handleUpdateTickers(state, tickers);
  if (hasTrades) state = Trades.handleUpdateTradesData(state, trades);
  if (hasDepths) state = Depth.handleUpdateDepthData(state, depths); // avg .5~1ms execution, new state size 300kb
  if (hasExe) state = handleUpdateExecute(state, execuInfo);
  if (hasAcc) state = handleUpdateAccoutInfo(state, accoutInfo);
  return state;
};

// eslint-disable-next-line import/prefer-default-export
export const reducer = (state, action) => {
  switch (action.type) {
    case actionTypes.WEB_SOCKET_RECEIVED_DEPTH:
      return Depth.handleUpdateDepthData(state, action.payload);
    case actionTypes.WEB_SOCKET_RECEIVED_TRADE:
      return Trades.handleUpdateTradesData(state, action.payload);
    case actionTypes.WEB_SOCKET_RECEIVED_TICKERS:
      return Tickers.handleUpdateTickersMap(state, action.payload);

    // depth streamer status
    case actionTypes.WEB_SOCKET_DEPTH_CONNECTED:
      return { ...state, depthSocketConnected: true };
    case actionTypes.WEB_SOCKET_DEPTH_DISCONNECTED:
      return { ...state, depthSocketConnected: false };
    // web socket actions
    case actionTypes.WEB_SOCKET_CONNECTED:
      return { ...state, socketConnected: true };
    case actionTypes.WEB_SOCKET_DISCONNECTED:
      return { ...state, socketConnected: false };
    case actionTypes.WEB_SOCKET_RECEIVED:
      return handleSocketReceive(state, action);

    // account,order,kline
    case actionTypes.WEB_SOCKET_MARGIN_UPDATE:
      return handleSocketReceiveMarginUpdate(state, action);
    case actionTypes.WEB_SOCKET_RECEIVED_BATCH:
      return handleSocketReceiveBatch(state, action);
    default:
      return state;
  }
};
